// Catálogo de marcadores laboratoriais.
//
// Os eixos vieram do prontuário real do caso (docs/PRODUCT.md §4.3): hemograma,
// inflamatório, renal, eletrólitos, hepático, gasometria. A lista cresce com o
// caso — por isso `marcador` é string no banco, e o significado mora aqui.
//
// A faixa de referência serve para PINTAR o valor fora do esperado, nunca para
// julgar: referência de laboratório varia, e quem interpreta é a equipe.
//
// E ela vem do laudo do HOSPITAL DE BASE, nao de uma tabela generica. O motivo
// e pratico: a familia tem o papel do laboratorio na mao e compara. Se aqui o
// potassio 3,2 sai marcado em vermelho e no laudo do hospital ele esta dentro
// do intervalo impresso (3,2 a 5,6), o sistema nao esta alertando — esta
// contradizendo o documento oficial, e quem perde a confianca e o sistema.
// Onde as duas coincidem nao ha o que escolher; onde divergem, vale o laudo de
// quem processou o exame dela.

export interface Marcador {
  chave: string;
  rotulo: string;
  unidade: string;
  grupo: 'hemograma' | 'inflamatorio' | 'renal' | 'eletrolitos' | 'hepatico' | 'gasometria' | 'outros';
  /** Faixa usual de adulto. Ausente quando depende demais do contexto. */
  referencia?: { min: number; max: number };
  /** Casas decimais na exibição. */
  casas?: number;
  /**
   * Como o marcador aparece escrito no prontuario. A evolucao medica e digitada
   * a mao, em abreviacao: "Hb 10,4 Leuco 10.600 Plaq 174.000". Sem esta lista, a
   * leitura por foto nao acha nada — e "Hb", "HB" e "hemoglobina" virariam
   * series diferentes.
   */
  sinonimos?: string[];
  /**
   * Menor mudança, na unidade do marcador, que já conta como "subiu" ou "caiu".
   * Sem ela, vale a regra geral dos 5%. Existe para o marcador de faixa estreita,
   * em que 5% é grande demais: o sódio caiu de 134 para 129 em três dias (26/09)
   * — 3,7%, "estável" pela regra geral — e isso é justamente o que a equipe vigia.
   */
  variacaoMinima?: number;
}

export const MARCADORES: Marcador[] = [
  // hemograma
  { chave: 'hemoglobina', rotulo: 'Hemoglobina', unidade: 'g/dL', grupo: 'hemograma', referencia: { min: 12, max: 16 }, casas: 1, sinonimos: ['Hb', 'HB'] },
  { chave: 'hematocrito', rotulo: 'Hematócrito', unidade: '%', grupo: 'hemograma', referencia: { min: 36, max: 46 }, casas: 1, sinonimos: ['Ht', 'HT'] },
  { chave: 'leucocitos', rotulo: 'Leucócitos', unidade: '/mm³', grupo: 'hemograma', referencia: { min: 5000, max: 10000 }, sinonimos: ['Leuco', 'Leucograma'] },
  { chave: 'plaquetas', rotulo: 'Plaquetas', unidade: '/mm³', grupo: 'hemograma', referencia: { min: 140000, max: 400000 }, sinonimos: ['Plaq'] },
  // A série vermelha inteira, que é o que explica a anemia dela: hemoglobina
  // baixa sozinha não diz se faltam células ou se as células estão pequenas.
  { chave: 'hemacias', rotulo: 'Hemácias', unidade: '10⁶/µL', grupo: 'hemograma', referencia: { min: 4, max: 5.2 }, casas: 2, sinonimos: ['Hemacias', 'Eritrocitos', 'RBC'] },
  { chave: 'rdw', rotulo: 'RDW', unidade: '%', grupo: 'hemograma', referencia: { min: 11.5, max: 14.5 }, casas: 1, sinonimos: ['RDW-CV'] },
  { chave: 'vcm', rotulo: 'VCM', unidade: 'fL', grupo: 'hemograma', referencia: { min: 78, max: 102 }, casas: 1, sinonimos: ['MCV'] },
  { chave: 'hcm', rotulo: 'HCM', unidade: 'pg', grupo: 'hemograma', referencia: { min: 25, max: 35 }, casas: 1, sinonimos: ['MCH'] },
  { chave: 'chcm', rotulo: 'CHCM', unidade: 'g/dL', grupo: 'hemograma', referencia: { min: 31, max: 37 }, casas: 1, sinonimos: ['MCHC'] },
  { chave: 'vpm', rotulo: 'VPM', unidade: 'fL', grupo: 'hemograma', referencia: { min: 7.2, max: 11 }, casas: 1, sinonimos: ['MPV'] },
  // Diferencial em valor ABSOLUTO (10³/µL), não em porcentagem: é o absoluto
  // que o laudo do Hospital de Base traz com faixa de referência, e comparar
  // porcentagem entre coletas engana quando o total de leucócitos muda.
  { chave: 'segmentados', rotulo: 'Neutrófilos segmentados', unidade: '10³/µL', grupo: 'hemograma', referencia: { min: 1.8, max: 7.7 }, casas: 2, sinonimos: ['Neutrofilos', 'Segm'] },
  { chave: 'linfocitos', rotulo: 'Linfócitos', unidade: '10³/µL', grupo: 'hemograma', referencia: { min: 1, max: 3 }, casas: 2, sinonimos: ['Linfo'] },
  { chave: 'monocitos', rotulo: 'Monócitos', unidade: '10³/µL', grupo: 'hemograma', referencia: { min: 0, max: 0.8 }, casas: 2, sinonimos: ['Mono'] },
  { chave: 'eosinofilos', rotulo: 'Eosinófilos', unidade: '10³/µL', grupo: 'hemograma', referencia: { min: 0.05, max: 0.7 }, casas: 2, sinonimos: ['Eosino'] },
  { chave: 'basofilos', rotulo: 'Basófilos', unidade: '10³/µL', grupo: 'hemograma', referencia: { min: 0, max: 0.2 }, casas: 2, sinonimos: ['Baso'] },

  // inflamatório
  { chave: 'pcr', rotulo: 'PCR', unidade: 'mg/dL', grupo: 'inflamatorio', referencia: { min: 0, max: 0.5 }, casas: 2, sinonimos: ['Proteina C reativa'] },
  // Sobe em infecção BACTERIANA, e pouco em viral ou em inflamação sem
  // infecção. É por isso que ela pesa na decisão de manter ou parar
  // antibiótico, enquanto a PCR sozinha não resolve essa dúvida.
  { chave: 'procalcitonina', rotulo: 'Procalcitonina', unidade: 'ng/mL', grupo: 'inflamatorio', referencia: { min: 0, max: 0.5 }, casas: 3, sinonimos: ['PCT'] },

  // renal
  { chave: 'ureia', rotulo: 'Ureia', unidade: 'mg/dL', grupo: 'renal', referencia: { min: 10, max: 50 }, sinonimos: ['Ur', 'U'] },
  { chave: 'creatinina', rotulo: 'Creatinina', unidade: 'mg/dL', grupo: 'renal', referencia: { min: 0.6, max: 1.1 }, casas: 2, sinonimos: ['Cr', 'Creat'] },
  { chave: 'tfg', rotulo: 'TFG', unidade: 'mL/min', grupo: 'renal', referencia: { min: 60, max: 120 } },

  // eletrólitos
  { chave: 'sodio', rotulo: 'Sódio', unidade: 'mEq/L', grupo: 'eletrolitos', referencia: { min: 135, max: 145 }, variacaoMinima: 3, sinonimos: ['Na'] },
  { chave: 'potassio', rotulo: 'Potássio', unidade: 'mEq/L', grupo: 'eletrolitos', referencia: { min: 3.2, max: 5.6 }, casas: 1, sinonimos: ['K'] },
  { chave: 'magnesio', rotulo: 'Magnésio', unidade: 'mg/dL', grupo: 'eletrolitos', referencia: { min: 1.6, max: 2.6 }, casas: 2, sinonimos: ['Mg'] },
  { chave: 'calcio', rotulo: 'Cálcio total', unidade: 'mg/dL', grupo: 'eletrolitos', referencia: { min: 8.5, max: 10.5 }, casas: 1, sinonimos: ['Ca'] },
  /**
   * O calcio da GASOMETRIA, que e outra medida — nao e o calcio total.
   *
   * Unidade diferente (mmol/L, nao mg/dL) e faixa dez vezes menor. Juntar os
   * dois numa serie so faria "1,0" aparecer como queda catastrofica ao lado
   * de um "8,5" que media outra coisa. Foi exatamente o que aconteceu ate
   * este marcador existir.
   */
  { chave: 'calcio_ionico', rotulo: 'Cálcio iônico', unidade: 'mmol/L', grupo: 'gasometria', referencia: { min: 1.12, max: 1.32 }, casas: 3, sinonimos: ['Ca ionico', 'Cai'] },
  { chave: 'fosforo', rotulo: 'Fósforo', unidade: 'mg/dL', grupo: 'eletrolitos', referencia: { min: 2.5, max: 4.5 }, casas: 2, sinonimos: ['P', 'Fosf'] },

  // hepático
  { chave: 'tgo', rotulo: 'TGO', unidade: 'U/L', grupo: 'hepatico', referencia: { min: 0, max: 40 }, sinonimos: ['AST'] },
  { chave: 'tgp', rotulo: 'TGP', unidade: 'U/L', grupo: 'hepatico', referencia: { min: 0, max: 41 }, sinonimos: ['ALT'] },
  { chave: 'fosfatase_alcalina', rotulo: 'Fosfatase alcalina', unidade: 'U/L', grupo: 'hepatico', referencia: { min: 65, max: 300 }, sinonimos: ['FA', 'Fosfatase alcalina', 'ALP'] },
  { chave: 'ggt', rotulo: 'Gama GT', unidade: 'U/L', grupo: 'hepatico', referencia: { min: 7, max: 32 }, sinonimos: ['GGT', 'Gama GT', 'Gama glutamil transferase'] },
  { chave: 'bilirrubina_total', rotulo: 'Bilirrubina total', unidade: 'mg/dL', grupo: 'hepatico', referencia: { min: 0.2, max: 1.2 }, casas: 2, sinonimos: ['BT'] },
  { chave: 'albumina', rotulo: 'Albumina', unidade: 'g/dL', grupo: 'hepatico', referencia: { min: 3.5, max: 5.2 }, casas: 2, sinonimos: ['Alb', 'Alumina'] },
  { chave: 'inr', rotulo: 'INR', unidade: '', grupo: 'hepatico', referencia: { min: 0.8, max: 1.2 }, casas: 2 },

  // gasometria
  { chave: 'ph', rotulo: 'pH', unidade: '', grupo: 'gasometria', referencia: { min: 7.35, max: 7.45 }, casas: 2 },
  { chave: 'pco2', rotulo: 'pCO2', unidade: 'mmHg', grupo: 'gasometria', referencia: { min: 35, max: 45 }, casas: 1 },
  { chave: 'po2', rotulo: 'pO2', unidade: 'mmHg', grupo: 'gasometria', referencia: { min: 80, max: 100 }, casas: 1 },
  { chave: 'hco3', rotulo: 'HCO3', unidade: 'mEq/L', grupo: 'gasometria', referencia: { min: 22, max: 26 }, casas: 1 },
  { chave: 'be', rotulo: 'BE', unidade: 'mEq/L', grupo: 'gasometria', referencia: { min: -2, max: 2 }, casas: 2 },
  { chave: 'lactato', rotulo: 'Lactato', unidade: 'mmol/L', grupo: 'gasometria', referencia: { min: 0, max: 2 }, casas: 1, sinonimos: ['Lac', 'Lacttao'] },
  { chave: 'saturacao_arterial', rotulo: 'SatO2 arterial', unidade: '%', grupo: 'gasometria', referencia: { min: 95, max: 100 }, casas: 1 },

  { chave: 'bilirrubina_direta', rotulo: 'Bilirrubina direta', unidade: 'mg/dL', grupo: 'hepatico', referencia: { min: 0, max: 0.3 }, casas: 2, sinonimos: ['BD'] },
  { chave: 'bilirrubina_indireta', rotulo: 'Bilirrubina indireta', unidade: 'mg/dL', grupo: 'hepatico', referencia: { min: 0.1, max: 0.8 }, casas: 2, sinonimos: ['BI'] },
  { chave: 'ttpa', rotulo: 'TTPA (relacao)', unidade: '', grupo: 'hepatico', referencia: { min: 0.8, max: 1.2 }, casas: 2, sinonimos: ['TTPAr', 'TTPA'] },
  { chave: 'clearance_creatinina', rotulo: 'Clearance de creatinina', unidade: 'mL/min', grupo: 'renal', referencia: { min: 60, max: 120 }, casas: 1, sinonimos: ['ClCr'] },
  { chave: 'svo2_central', rotulo: 'SvcO2 (venosa central)', unidade: '%', grupo: 'gasometria', referencia: { min: 70, max: 80 }, casas: 1, sinonimos: ['SvcO2', 'SvO2'] },
  /*
   * Gasometria VENOSA tem marcadores proprios, e isso nao e preciosismo.
   *
   * Sangue venoso ja entregou o oxigenio aos tecidos: pO2 de 47 e saturacao de
   * 88 sao NORMAIS nele. Lancados nos marcadores arteriais — onde o esperado e
   * 80 a 100 e 95 a 100 — sairiam pintados de vermelho no relatorio que a
   * familia poe na mao do medico. Alarme falso num documento assim nao e um
   * detalhe: e o que faz o leitor deixar de confiar no resto da pagina.
   *
   * As faixas sao as impressas no laudo do Hospital de Base para gasometria
   * venosa; as do pH e do pCO2 praticamente coincidem com as arteriais e por
   * isso nao ganharam entrada separada.
   */
  { chave: 'po2_venoso', rotulo: 'pO2 (venoso)', unidade: 'mmHg', grupo: 'gasometria', referencia: { min: 30, max: 50 }, casas: 1 },
  { chave: 'saturacao_venosa', rotulo: 'SatO2 (venosa)', unidade: '%', grupo: 'gasometria', referencia: { min: 75, max: 99 }, casas: 1 },
  { chave: 'gap_co2', rotulo: 'Gap de CO2', unidade: 'mmHg', grupo: 'gasometria', referencia: { min: 0, max: 6 }, casas: 1, sinonimos: ['GapCO2', 'DeltaCO2'] },
  { chave: 'anion_gap', rotulo: 'Ânion gap', unidade: 'mmol/L', grupo: 'gasometria', referencia: { min: 8, max: 16 }, casas: 1, sinonimos: ['AG', 'Anion gap'] },

  // outros
  { chave: 'cpk', rotulo: 'CPK', unidade: 'U/L', grupo: 'outros', referencia: { min: 26, max: 192 } },
  { chave: 'glicemia_lab', rotulo: 'Glicemia (lab)', unidade: 'mg/dL', grupo: 'outros', referencia: { min: 74, max: 99 }, sinonimos: ['Glic', 'HGT'] },
  // Sobe quando o coracao trabalha sob pressao. Com a fracao de ejecao dela, e
  // o numero que acompanha a insuficiencia cardiaca ao longo do tempo.
  // A faixa e a de quem tem mais de 60 anos ou esta em grupo de risco (30 a 60),
  // nao a da populacao geral (acima de 20) — e a que o laudo aplica ao caso dela.
  // O painel do ferro, pedido para investigar a anemia. As faixas sao as do
  // laudo do Hospital de Base, e a da ferritina e a FEMININA (13 a 150) — a
  // masculina vai ate 400 e marcaria como normal um valor que nao e o dela.
  { chave: 'ferro_serico', rotulo: 'Ferro sérico', unidade: 'µg/dL', grupo: 'hemograma', referencia: { min: 50, max: 170 }, sinonimos: ['Ferro'] },
  { chave: 'ferritina', rotulo: 'Ferritina', unidade: 'ng/mL', grupo: 'hemograma', referencia: { min: 13, max: 150 }, casas: 1 },
  { chave: 'capacidade_total_ferro', rotulo: 'Capacidade total de fixação de ferro', unidade: 'µg/dL', grupo: 'hemograma', referencia: { min: 250, max: 425 }, sinonimos: ['TIBC'] },
  { chave: 'capacidade_livre_ferro', rotulo: 'Capacidade livre de combinação do ferro', unidade: 'µg/dL', grupo: 'hemograma', referencia: { min: 140, max: 346 }, sinonimos: ['UIBC'] },
  { chave: 'saturacao_transferrina', rotulo: 'Saturação de transferrina', unidade: '%', grupo: 'hemograma', referencia: { min: 15, max: 50 }, casas: 1, sinonimos: ['IST'] },
  { chave: 'vitamina_b12', rotulo: 'Vitamina B12', unidade: 'pg/mL', grupo: 'outros', referencia: { min: 114, max: 890 }, sinonimos: ['B12', 'Cobalamina'] },
  { chave: 'tsh', rotulo: 'TSH', unidade: 'µUI/mL', grupo: 'outros', referencia: { min: 0.27, max: 4.2 }, casas: 2, sinonimos: ['TSH', 'Hormonio tireoestimulante'] },
  { chave: 't4_livre', rotulo: 'T4 livre', unidade: 'ng/dL', grupo: 'outros', referencia: { min: 0.92, max: 1.68 }, casas: 2, sinonimos: ['T4L', 'T4 livre', 'Tiroxina livre'] },
  { chave: 'vitamina_d', rotulo: 'Vitamina D (25OH)', unidade: 'ng/mL', grupo: 'outros', referencia: { min: 30, max: 60 }, casas: 1, sinonimos: ['25OH', 'Vit D'] },
  { chave: 'probnp', rotulo: 'NT-proBNP', unidade: 'pg/mL', grupo: 'outros', referencia: { min: 0, max: 125 }, sinonimos: ['ProBNP', 'BNP', 'NT-proBNP'] },
  { chave: 'cloretos', rotulo: 'Cloretos', unidade: 'mmol/L', grupo: 'eletrolitos', referencia: { min: 95, max: 110 }, casas: 1, sinonimos: ['Cl', 'Cloro'] },
  // O TAP vem em tres numeros no laudo. O INR ja existe acima; estes dois sao
  // o tempo bruto e a atividade, que o medico olha junto quando ha
  // anticoagulante em uso.
  { chave: 'tap_tempo', rotulo: 'TAP (tempo)', unidade: 's', grupo: 'hepatico', referencia: { min: 9.8, max: 12.1 }, casas: 1, sinonimos: ['Tempo de protrombina'] },
  { chave: 'tap_atividade', rotulo: 'TAP (atividade)', unidade: '%', grupo: 'hepatico', referencia: { min: 70, max: 130 }, casas: 1, sinonimos: ['Atividade de protrombina'] },
];

export const ROTULO_GRUPO: Record<Marcador['grupo'], string> = {
  hemograma: 'Hemograma',
  inflamatorio: 'Inflamatório',
  renal: 'Função renal',
  eletrolitos: 'Eletrólitos',
  hepatico: 'Hepático',
  gasometria: 'Gasometria',
  outros: 'Outros',
};

const PORCHAVE = new Map(MARCADORES.map((m) => [m.chave, m]));
export const acharMarcador = (chave: string): Marcador | undefined => PORCHAVE.get(chave);

const ACENTOS = /[\u0300-\u036f]/g;
const normalizar = (texto: string) =>
  texto.trim().toLowerCase().normalize('NFD').replace(ACENTOS, '')
    .replace(/[^a-z0-9]/g, ''); // tira pontuacao: "TTPAr" e "ttpa-r" sao o mesmo

const PORTEXTO = new Map<string, Marcador>();
for (const m of MARCADORES) {
  PORTEXTO.set(normalizar(m.chave), m);
  PORTEXTO.set(normalizar(m.rotulo), m);
  for (const apelido of m.sinonimos ?? []) PORTEXTO.set(normalizar(apelido), m);
}

/**
 * Acha o marcador pelo jeito como ele aparece ESCRITO no prontuario: "Hb",
 * "Leuco", "Ur", "Creatinina". A leitura de exame por foto precisa disto, porque
 * o texto vem do jeito que o medico digitou, nao do jeito que o banco guarda.
 */
export const acharMarcadorPorTexto = (texto: string): Marcador | undefined =>
  PORTEXTO.get(normalizar(texto));

export type ForaDaFaixa = 'alto' | 'baixo' | 'dentro' | 'desconhecido';

/** Só diz se está fora da faixa usual. Interpretar é da equipe, não do sistema. */
export function compararComReferencia(chave: string, valor: number): ForaDaFaixa {
  const marcador = PORCHAVE.get(chave);
  if (!marcador?.referencia) return 'desconhecido';
  if (valor > marcador.referencia.max) return 'alto';
  if (valor < marcador.referencia.min) return 'baixo';
  return 'dentro';
}

export function formatarValor(chave: string, valor: number): string {
  const casas = PORCHAVE.get(chave)?.casas ?? 0;
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/**
 * Direção da mudança entre duas coletas — o que a tendência mostra. Com a chave,
 * o marcador que tem `variacaoMinima` usa ela no lugar dos 5%.
 */
export function tendencia(anterior: number, atual: number, chave?: string): 'subiu' | 'caiu' | 'estavel' {
  const minima = chave ? PORCHAVE.get(chave)?.variacaoMinima : undefined;
  if (minima !== undefined) {
    if (Math.abs(atual - anterior) < minima) return 'estavel';
    return atual > anterior ? 'subiu' : 'caiu';
  }
  if (anterior === 0) return atual === 0 ? 'estavel' : 'subiu';
  const variacao = Math.abs(atual - anterior) / Math.abs(anterior);
  if (variacao < 0.05) return 'estavel'; // abaixo de 5% é ruído de laboratório
  return atual > anterior ? 'subiu' : 'caiu';
}
